
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import hostlink from '../Hostlink/hostlink';
import * as XLSX from 'xlsx';
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    StyleSheet
} from '@react-pdf/renderer';

const Rpt_SaleReport = () => {

    const usershopid = localStorage.getItem('MYPCKARTUSER')
        ? JSON.parse(localStorage.getItem('MYPCKARTUSER'))[0].id
        : '0';

    const today = new Date();

    const [data, setData] = useState([]);
    const [mobileNo, setmobileNo] = useState([]);
    const [filteredDataMob, setFilteredDataMob] = useState([]);

    const [loading, setLoading] = useState(false);

    // Customer search
    const [searchTerm, setSearchTerm] = useState('');

    // Lot No search
    const [lotSearch, setLotSearch] = useState('');

    const [frmdata, setfrmdata] = useState({
        fromdate: today.toISOString().split('T')[0],
        todate: today.toISOString().split('T')[0],
        deptcode: 0,
        status: 0
    });

    const [itemdetail, setitemdetail] = useState({
        mobno: '',
        custname: '',
        custadd: '',
        custstate: '',
        avg: '',
        style: '',
        size: '',
        pcs: '',
        rawcode: 0,
        washqty: 0,
        Washername: '',
        Washercode: 0,
        rawname: '',
        barcode: '',
        sortno: '',
        billno: '',
        billdate: today.toISOString().split('T')[0],
        mtr: '',
        roll: '',
        qty: 1,
        rate: 0,
        discperc: 0,
        taxable: 0,
        gst: 0,
        gstamt: 0,
        ittotal: 0,
        totqty: 0,
        totgst: 0,
        totordamt: 0,
        rcode: '',
        roomnoview: ''
    });

    const {
        fromdate,
        todate
    } = frmdata;

    const tableRef = useRef(null);

    // =========================================================
    // Number Format
    // =========================================================
    const formatNumber = (value) => {

        return Number(value || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

    };

    // =========================================================
    // Change Date
    // =========================================================
    const onChangevalue = (e) => {

        const { name, value } = e.target;

        const sanitizedValue = value.replace(
            /['"!`@#$%^&*+{}|;:,.<>?=]/g,
            ''
        );

        setfrmdata({
            ...frmdata,
            [name]: sanitizedValue
        });

    };

    // =========================================================
    // Customer Search - LOCAL ONLY
    // =========================================================
    const handleSearch = (e) => {

        const value = e.target.value;

        setSearchTerm(value);

        setitemdetail((prev) => ({
            ...prev,
            mobno: value
        }));

        const query = value.toLowerCase().trim();

        if (!query) {
            setFilteredDataMob([]);
            return;
        }

        const matches = mobileNo.filter((item) => {

            const mobno = String(item.mobno || '').toLowerCase();
            const custname = String(item.custname || '').toLowerCase();
            const custadd = String(item.custadd || '').toLowerCase();

            return (
                mobno.includes(query) ||
                custname.includes(query) ||
                custadd.includes(query)
            );
        });

        setFilteredDataMob(matches.slice(0, 20));
    };


    // =========================================================
    // Select Customer - LOCAL ONLY
    // =========================================================
    const handleSelect = (item) => {

        const selectedCustomer = item.custname || '';
        const selectedMobile = item.mobno || '';

        setSearchTerm(selectedCustomer);

        setitemdetail((prev) => ({
            ...prev,
            mobno: selectedMobile,
            custname: selectedCustomer,
            custadd: item.custadd || '',
            custstate: item.custstate || ''
        }));

        setFilteredDataMob([]);
    };


    // =========================================================
    // Load Sale Data - ONLY when Show is clicked / initial load
    // Customer and Lot searches are handled locally.
    // =========================================================
    const loaddata = async () => {

        try {

            setLoading(true);

            const response = await axios.get(
                `${hostlink}/PurchaseMasviewrpt/5/${usershopid}/${fromdate}/${todate}`
            );

            setData(response.data || []);

        } catch (error) {

            console.error('Sale Report Error:', error);
            setData([]);

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // Initial Load
    // Customer master + Sale report are loaded once.
    // =========================================================
    const initialLoadRef = useRef(false);

    useEffect(() => {

        if (initialLoadRef.current) {
            return;
        }

        initialLoadRef.current = true;

        const loadInitialData = async () => {

            setLoading(true);

            try {

                const [customerResponse, saleResponse] = await Promise.all([
                    axios.get(`${hostlink}/MobileNoSearch/${usershopid}`),
                    axios.get(
                        `${hostlink}/PurchaseMasviewrpt/5/${usershopid}/${fromdate}/${todate}`
                    )
                ]);

                setmobileNo(customerResponse.data || []);
                setData(saleResponse.data || []);

            } catch (error) {

                console.error('Sale Report Initial Load Error:', error);

                // Keep whichever response succeeded if Promise.all fails.
                try {
                    const customerResponse = await axios.get(
                        `${hostlink}/MobileNoSearch/${usershopid}`
                    );
                    setmobileNo(customerResponse.data || []);
                } catch (customerError) {
                    console.error('Customer Master Error:', customerError);
                    setmobileNo([]);
                }

                try {
                    const saleResponse = await axios.get(
                        `${hostlink}/PurchaseMasviewrpt/5/${usershopid}/${fromdate}/${todate}`
                    );
                    setData(saleResponse.data || []);
                } catch (saleError) {
                    console.error('Sale Report Error:', saleError);
                    setData([]);
                }

            } finally {

                setLoading(false);
            }
        };

        loadInitialData();

    }, []);

    // =========================================================
    // Local Filters - Customer + Lot No
    // No API call here.
    // =========================================================
    const filteredData = useMemo(() => {

        const lotQuery = lotSearch.trim().toLowerCase();
        const customerQuery = searchTerm.trim().toLowerCase();

        return data.filter((res) => {

            const lotNo = String(
                res?.billno ??
                res?.purchaseMas?.billno ??
                ''
            ).toLowerCase();

            const mobile = String(res?.mobno || '').toLowerCase();
            const customer = String(res?.custname || '').toLowerCase();
            const address = String(res?.custadd || '').toLowerCase();

            const lotMatch =
                !lotQuery || lotNo.includes(lotQuery);

            const customerMatch =
                !customerQuery ||
                mobile.includes(customerQuery) ||
                customer.includes(customerQuery) ||
                address.includes(customerQuery);

            return lotMatch && customerMatch;
        });

    }, [data, lotSearch, searchTerm]);

    // =========================================================
    // Summary
    // =========================================================
    const summary = useMemo(() => {

        const totalPcs = filteredData.reduce(
            (total, res) =>
                total + Number(res.salestock || 0),
            0
        );

        const uniqueLots = new Set(
            filteredData.map(
                res => res?.billno
            )
        ).size;

        const uniqueCustomers = new Set(
            filteredData
                .map(res => res?.custname)
                .filter(Boolean)
        ).size;

        return {
            records: filteredData.length,
            lots: uniqueLots,
            customers: uniqueCustomers,
            pcs: totalPcs
        };

    }, [filteredData]);

    // =========================================================
    // Excel Export
    // =========================================================
    const exportToExcel = () => {

        if (!tableRef.current) {
            return;
        }

        const ws = XLSX.utils.table_to_sheet(
            tableRef.current
        );

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            'Sale Report'
        );

        XLSX.writeFile(
            wb,
            'Sale Report.xlsx'
        );

    };

    // =========================================================
    // PDF filename
    // =========================================================
    const pdfFileName = lotSearch.trim()
        ? `Sale Report - Lot ${lotSearch}.pdf`
        : searchTerm.trim()
            ? `Sale Report - ${searchTerm.trim()}.pdf`
            : 'Sale Report.pdf';

    return (

        <div
            className="container-fluid py-3"
            style={{
                background: '#f6f8fb',
                minHeight: '100vh'
            }}
        >

            {/* =====================================================
                HEADER + FILTER
            ====================================================== */}
            <div
                className="card border-0 mb-3"
                style={{
                    borderRadius: '18px',
                    overflow: 'hidden',
                    boxShadow:
                        '0 6px 24px rgba(15,23,42,0.07)'
                }}
            >

                {/* HEADER */}
                <div
                    style={{
                        background:
                            'linear-gradient(135deg,#0d6efd,#315efb)',
                        padding: '18px 20px',
                        color: '#fff'
                    }}
                >

                    <div
                        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                    >

                        <div className="d-flex align-items-center">

                            <div
                                className="d-flex align-items-center justify-content-center me-3"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '14px',
                                    background:
                                        'rgba(255,255,255,0.18)'
                                }}
                            >
                                <i
                                    className="fa-solid fa-book"
                                    style={{
                                        fontSize: '21px'
                                    }}
                                ></i>
                            </div>

                            <div>

                                <div
                                    style={{
                                        fontSize: '20px',
                                        fontWeight: '700'
                                    }}
                                >
                                    Sale Report
                                </div>

                                <div
                                    style={{
                                        fontSize: '12px',
                                        opacity: 0.85
                                    }}
                                >
                                    Customer-wise sale details
                                </div>

                            </div>

                        </div>

                        <div
                            className="badge rounded-pill"
                            style={{
                                background:
                                    'rgba(255,255,255,0.16)',
                                padding: '9px 13px',
                                fontSize: '11px'
                            }}
                        >
                            {filteredData.length} Records
                        </div>

                    </div>

                </div>


                {/* =================================================
                    FILTER AREA
                ================================================== */}
                <div className="card-body p-3">

                    <div className="row g-2 align-items-end">

                        {/* LOT SEARCH */}
                        <div className="col-12 col-md-4 col-lg-3">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                Lot No
                            </label>

                            <div className="input-group input-group-sm">

                                <span
                                    className="input-group-text"
                                    style={{
                                        background: '#fff',
                                        borderRadius:
                                            '10px 0 0 10px'
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-magnifying-glass text-primary"
                                    ></i>
                                </span>

                                <input
                                    type="text"
                                    value={lotSearch}
                                    onChange={(e) =>
                                        setLotSearch(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Search Lot No..."
                                    className="form-control"
                                    style={{
                                        borderRadius:
                                            lotSearch
                                                ? '0'
                                                : '0 10px 10px 0'
                                    }}
                                />

                                {lotSearch && (

                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() =>
                                            setLotSearch('')
                                        }
                                        style={{
                                            borderRadius:
                                                '0 10px 10px 0'
                                        }}
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>

                                )}

                            </div>

                        </div>


                        {/* CUSTOMER SEARCH */}
                        <div className="col-12 col-md-4 col-lg-3">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                Customer / Mobile
                            </label>

                            <input
                                type="text"
                                placeholder="Search Name / Mobile..."
                                className="form-control form-control-sm"
                                value={searchTerm}
                                onChange={handleSearch}
                                autoComplete="off"
                                style={{
                                    borderRadius: '10px'
                                }}
                            />

                            {/* CUSTOMER SUGGESTIONS */}
                            {filteredDataMob.length > 0 && (

                                <div
                                    style={{
                                        position: 'absolute',
                                        zIndex: 20,
                                        width: '300px',
                                        maxWidth: '90%',
                                        background: '#fff',
                                        border:
                                            '1px solid #e5e7eb',
                                        borderRadius: '10px',
                                        marginTop: '4px',
                                        boxShadow:
                                            '0 8px 25px rgba(0,0,0,0.12)',
                                        overflow: 'hidden'
                                    }}
                                >

                                    <div
                                        style={{
                                            maxHeight: '180px',
                                            overflowY: 'auto'
                                        }}
                                    >

                                        {filteredDataMob.map(
                                            (item, index) => (

                                                <div
                                                    key={index}
                                                    onClick={() =>
                                                        handleSelect(item)
                                                    }
                                                    style={{
                                                        cursor:
                                                            'pointer',
                                                        padding:
                                                            '9px 12px',
                                                        borderBottom:
                                                            '1px solid #f1f5f9',
                                                        fontSize:
                                                            '12px'
                                                    }}
                                                >

                                                    <div
                                                        className="fw-bold"
                                                    >
                                                        {item.custname}
                                                    </div>

                                                    <div className="text-muted">
                                                        {item.mobno}
                                                        {' / '}
                                                        {item.custstate}
                                                    </div>

                                                </div>

                                            )
                                        )}

                                    </div>

                                </div>

                            )}

                        </div>


                        {/* FROM DATE */}
                        <div className="col-6 col-md-2 col-lg-2">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                From Date
                            </label>

                            <input
                                type="date"
                                name="fromdate"
                                value={fromdate}
                                max={todate}
                                onChange={onChangevalue}
                                className="form-control form-control-sm"
                                style={{
                                    borderRadius: '10px'
                                }}
                            />

                        </div>


                        {/* TO DATE */}
                        <div className="col-6 col-md-2 col-lg-2">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                To Date
                            </label>

                            <input
                                type="date"
                                name="todate"
                                value={todate}
                                min={fromdate}
                                onChange={onChangevalue}
                                className="form-control form-control-sm"
                                style={{
                                    borderRadius: '10px'
                                }}
                            />

                        </div>


                        {/* BUTTONS */}
                        <div className="col-12 col-lg-2">

                            <div className="d-flex gap-2">

                                <button
                                    onClick={loaddata}
                                    className="btn btn-primary btn-sm px-3"
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                            ></span>
                                            Loading
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-magnifying-glass-chart me-1"></i>
                                            Show
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={exportToExcel}
                                    className="btn btn-outline-success btn-sm px-3"
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: '600'
                                    }}
                                >
                                    <i className="fa-solid fa-file-excel me-1"></i>
                                    Excel
                                </button>

                                <PDFDownloadLink
                                    document={
                                        <PDFDocument
                                            data={filteredData}
                                            fromdate={fromdate}
                                            todate={todate}
                                        />
                                    }
                                    fileName={pdfFileName}
                                    style={{
                                        textDecoration: 'none'
                                    }}
                                >
                                    {({ loading: pdfLoading }) => (

                                        <button
                                            className="btn btn-outline-danger btn-sm px-3"
                                            style={{
                                                borderRadius: '10px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            <i className="fa-solid fa-file-pdf me-1"></i>
                                            {pdfLoading
                                                ? 'Loading'
                                                : 'PDF'}
                                        </button>

                                    )}
                                </PDFDownloadLink>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* =====================================================
                KPI CARDS
            ====================================================== */}
            <div className="row g-2 mb-3">

                {/* LOTS */}
                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Lots"
                        value={summary.lots}
                        icon="fa-boxes-stacked"
                        subtitle="Unique sale lots"
                    />

                </div>

                {/* RECORDS */}
                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Records"
                        value={summary.records}
                        icon="fa-list"
                        subtitle="Sale records"
                    />

                </div>

                {/* CUSTOMERS */}
                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Customers"
                        value={summary.customers}
                        icon="fa-users"
                        subtitle="Unique customers"
                    />

                </div>

                {/* PCS */}
                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Total Pcs"
                        value={formatNumber(summary.pcs)}
                        icon="fa-cubes"
                        subtitle="Total sold pieces"
                    />

                </div>

            </div>


            {/* =====================================================
                ACTIVE SEARCH
            ====================================================== */}
            {(lotSearch || searchTerm) && (

                <div
                    className="bg-white mb-3 px-3 py-2"
                    style={{
                        borderRadius: '12px',
                        border:
                            '1px solid #e9edf3',
                        boxShadow:
                            '0 3px 12px rgba(0,0,0,0.04)'
                    }}
                >

                    <div
                        className="d-flex align-items-center flex-wrap gap-2"
                    >

                        <span
                            className="text-muted"
                            style={{
                                fontSize: '11px',
                                fontWeight: '600'
                            }}
                        >
                            Active Search:
                        </span>

                        {lotSearch && (

                            <span className="badge bg-primary rounded-pill">
                                Lot No: {lotSearch}
                            </span>

                        )}

                        {searchTerm && (

                            <span className="badge bg-info rounded-pill">
                                Customer: {searchTerm}
                            </span>

                        )}

                        <span
                            className="text-muted"
                            style={{
                                fontSize: '11px'
                            }}
                        >
                            {filteredData.length} result(s)
                        </span>

                    </div>

                </div>

            )}


            {/* =====================================================
                TABLE
            ====================================================== */}
            <div
                className="card border-0"
                style={{
                    borderRadius: '18px',
                    overflow: 'hidden',
                    boxShadow:
                        '0 6px 24px rgba(15,23,42,0.07)'
                }}
            >

                <div
                    className="card-header bg-white border-0 d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{
                        padding: '15px 18px'
                    }}
                >

                    <div>

                        <div
                            className="fw-bold"
                            style={{
                                color: '#1f2937'
                            }}
                        >
                            Sale Details
                        </div>

                        <small className="text-muted">
                            Lot-wise customer sale details
                        </small>

                    </div>

                    <div
                        className="badge rounded-pill"
                        style={{
                            background: '#eef4ff',
                            color: '#2563eb',
                            padding: '8px 12px'
                        }}
                    >
                        {filteredData.length} rows
                    </div>

                </div>


                <div
                    style={{
                        overflowX: 'auto',
                        maxHeight: '65vh'
                    }}
                >

                    <table
                        ref={tableRef}
                        className="table table-hover align-middle mb-0"
                        style={{
                            minWidth: '1100px',
                            fontSize: '13px'
                        }}
                    >

                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                background: '#f1f5f9'
                            }}
                        >

                            <tr>

                                <th className="text-start">
                                    Lot No
                                </th>

                                <th className="text-start">
                                    Date
                                </th>

                                <th className="text-start">
                                    Cust Mob
                                </th>

                                <th className="text-start">
                                    Cust Name
                                </th>

                                <th className="text-start">
                                    Cust Add
                                </th>

                                <th className="text-start">
                                    Cust State
                                </th>

                                <th className="text-start">
                                    Style
                                </th>

                                <th className="text-start">
                                    Size
                                </th>

                                <th className="text-end">
                                    Pcs
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center"
                                        style={{
                                            padding: '50px'
                                        }}
                                    >

                                        <div
                                            className="spinner-border text-primary"
                                            role="status"
                                        ></div>

                                        <div
                                            className="text-muted mt-2"
                                            style={{
                                                fontSize: '12px'
                                            }}
                                        >
                                            Loading sale report...
                                        </div>

                                    </td>

                                </tr>

                            ) : filteredData.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center"
                                        style={{
                                            padding: '60px 20px'
                                        }}
                                    >

                                        <div
                                            className="d-flex align-items-center justify-content-center mx-auto mb-3"
                                            style={{
                                                width: '58px',
                                                height: '58px',
                                                borderRadius: '18px',
                                                background: '#f1f5f9',
                                                color: '#94a3b8'
                                            }}
                                        >
                                            <i
                                                className="fa-solid fa-box-open"
                                                style={{
                                                    fontSize: '24px'
                                                }}
                                            ></i>
                                        </div>

                                        <div className="fw-bold text-dark">
                                            No sale records found
                                        </div>

                                        <div
                                            className="text-muted mt-1"
                                            style={{
                                                fontSize: '12px'
                                            }}
                                        >
                                            Try another Lot No,
                                            customer or date range.
                                        </div>

                                    </td>

                                </tr>

                            ) : (

                                filteredData.map(
                                    (res, index) => {

                                        const previous =
                                            index > 0
                                                ? filteredData[index - 1]
                                                : null;

                                        const isDuplicate =
                                            previous &&
                                            previous.billno ===
                                            res.billno;

                                        return (

                                            <tr key={index}>

                                                <td
                                                    className="text-start fw-semibold"
                                                    style={{
                                                        color:
                                                            '#2563eb',
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {isDuplicate
                                                        ? ''
                                                        : res.billno}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {isDuplicate
                                                        ? ''
                                                        : res.billdate
                                                            ? new Date(
                                                                res.billdate
                                                            ).toLocaleDateString(
                                                                'en-GB',
                                                                {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: '2-digit'
                                                                }
                                                            )
                                                        : '-'}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {isDuplicate
                                                        ? ''
                                                        : res.mobno}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.custname}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.custadd}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.custstate}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.style}
                                                </td>

                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.size}
                                                </td>

                                                <td
                                                    className="text-end fw-semibold"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {formatNumber(
                                                        res.salestock
                                                    )}
                                                </td>

                                            </tr>

                                        );

                                    }
                                )

                            )}

                        </tbody>


                        {!loading &&
                            filteredData.length > 0 && (

                                <tfoot>

                                    <tr
                                        style={{
                                            position:
                                                'sticky',
                                            bottom: 0,
                                            zIndex: 4,
                                            background:
                                                '#eaf2ff',
                                            borderTop:
                                                '2px solid #cbd5e1'
                                        }}
                                    >

                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>

                                        <td
                                            className="text-end fw-bold"
                                            style={{
                                                padding:
                                                    '13px 14px'
                                            }}
                                        >
                                            Total :
                                        </td>

                                        <td
                                            className="text-end fw-bold"
                                            style={{
                                                padding:
                                                    '13px 14px'
                                            }}
                                        >
                                            {formatNumber(
                                                summary.pcs
                                            )}
                                        </td>

                                    </tr>

                                </tfoot>
                            )}

                    </table>

                </div>

            </div>

        </div>
    );
};


// =============================================================
// SUMMARY CARD
// =============================================================
const SummaryCard = ({
    title,
    value,
    icon,
    subtitle
}) => {

    return (

        <div
            className="bg-white h-100"
            style={{
                borderRadius: '16px',
                padding: '16px',
                boxShadow:
                    '0 4px 18px rgba(0,0,0,0.06)',
                border:
                    '1px solid #edf0f5'
            }}
        >

            <div
                className="d-flex justify-content-between align-items-center"
            >

                <div>

                    <div
                        className="text-muted"
                        style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform:
                                'uppercase',
                            letterSpacing:
                                '0.5px'
                        }}
                    >
                        {title}
                    </div>

                    <div
                        className="fw-bold mt-1"
                        style={{
                            fontSize: '20px',
                            color: '#1f2937'
                        }}
                    >
                        {value}
                    </div>

                    <div
                        className="text-muted mt-1"
                        style={{
                            fontSize: '10px'
                        }}
                    >
                        {subtitle}
                    </div>

                </div>

                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background:
                            '#eef4ff',
                        color:
                            '#2563eb'
                    }}
                >
                    <i
                        className={`fa-solid ${icon}`}
                    ></i>
                </div>

            </div>

        </div>
    );
};


// =============================================================
// PDF DOCUMENT
// =============================================================
const PDFDocument = ({
    data,
    fromdate,
    todate
}) => {

    const totalPcs = data.reduce(
        (total, res) =>
            total + Number(res.salestock || 0),
        0
    );

    const formatNumber = (value) =>
        Number(value || 0).toFixed(2);

    return (

        <Document>

            <Page
                orientation="landscape"
                size="A4"
                style={pdfStyles.container}
            >

                <Text style={pdfStyles.title}>
                    Sale Report
                </Text>

                <Text style={pdfStyles.subTitle}>
                    From : {fromdate} To : {todate}
                </Text>

                <View style={pdfStyles.table}>

                    {/* HEADER */}
                    <View style={pdfStyles.tableRow}>

                        <Text style={pdfStyles.headerCell}>
                            Lot No
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Date
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust Mob
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust Name
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust Add
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust State
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Style
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Size
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Pcs
                        </Text>

                    </View>

                    {/* DATA */}
                    {data.map((res, index) => {

                        const previous =
                            index > 0
                                ? data[index - 1]
                                : null;

                        const isDuplicate =
                            previous &&
                            previous.billno ===
                            res.billno;

                        const date =
                            res.billdate
                                ? new Date(
                                    res.billdate
                                ).toLocaleDateString(
                                    'en-GB',
                                    {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit'
                                    }
                                )
                                : '-';

                        return (

                            <View
                                key={index}
                                style={pdfStyles.tableRow}
                            >

                                <Text style={pdfStyles.cell}>
                                    {isDuplicate
                                        ? ''
                                        : res.billno}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {isDuplicate
                                        ? ''
                                        : date}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {isDuplicate
                                        ? ''
                                        : res.mobno}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {res.custname}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {res.custadd}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {res.custstate}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {res.style}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {res.size}
                                </Text>

                                <Text style={pdfStyles.cell}>
                                    {formatNumber(
                                        res.salestock
                                    )}
                                </Text>

                            </View>

                        );

                    })}

                    {/* TOTAL */}
                    <View style={pdfStyles.totalRow}>

                        <Text style={pdfStyles.cell}></Text>
                        <Text style={pdfStyles.cell}></Text>
                        <Text style={pdfStyles.cell}></Text>
                        <Text style={pdfStyles.cell}></Text>
                        <Text style={pdfStyles.cell}></Text>
                        <Text style={pdfStyles.cell}></Text>
                        <Text style={pdfStyles.cell}></Text>

                        <Text style={pdfStyles.totalCell}>
                            Total :
                        </Text>

                        <Text style={pdfStyles.totalCell}>
                            {formatNumber(totalPcs)}
                        </Text>

                    </View>

                </View>

            </Page>

        </Document>
    );
};


// =============================================================
// PDF STYLES
// =============================================================
const pdfStyles = StyleSheet.create({

    container: {
        padding: 20
    },

    title: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 6,
        textAlign: 'center'
    },

    subTitle: {
        fontSize: 9,
        marginBottom: 15,
        textAlign: 'center'
    },

    table: {
        display: 'table',
        width: '100%',
        borderWidth: 1,
        borderColor: '#444'
    },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#999'
    },

    headerCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#999'
    },

    cell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#ddd'
    },

    totalRow: {
        flexDirection: 'row',
        backgroundColor: '#eaf2ff',
        borderTopWidth: 1,
        borderColor: '#555'
    },

    totalCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#999'
    }

});

export default Rpt_SaleReport;

